import { useMemo } from "react";
import { useSelector } from "react-redux";
import useUserAuthentication from "../../_hooks/useUserAuthentication";
import { OrganizationType } from "../../_statics/OrganizationType";
import { UserRole } from "../../_statics/UserStatics";

const ROLE_LABELS = {
    [UserRole.SUPER_USER]: "Super User",
    [UserRole.TECHNICAL_USER]: "Technischer Benutzer",
    [UserRole.ADMINISTRATOR]: "Admin",
    [UserRole.REGULAR]: "Benutzer",
};

export const useUserDropdownUtils = () => {
    const clientReducer = useSelector((state) => state.client);
    const user = useSelector((state) => state.authentication.user);
    const { isSuperUser, isInAgency, selectedOrganization } = useUserAuthentication();

    const isTaxAgency = useMemo(
        () =>
            user &&
            user.selectedOrganization &&
            user.selectedOrganization.organizationType === OrganizationType.TAX_AGENCY,
        [user]
    );

    const userName = useMemo(() => [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "", [
        user,
    ]);

    // "Admin · Maniko Nails GmbH", as the prototype's menu header shows. Falls
    // back to the organization alone when the role is unknown.
    const userSubline = useMemo(() => {
        const role = ROLE_LABELS[user?.userRole];
        const organization = selectedOrganization?.name;
        return [role, organization].filter(Boolean).join(" · ");
    }, [user, selectedOrganization]);

    // Order and labels follow the prototype's account menu.
    const userDropdownItems = useMemo(() => {
        let items = [
            {
                name: "Profil",
                url: "/user",
                dataCy: "user-profile-button",
                icon: "fas fa-user",
                index: 1,
            },
            {
                name: "Account und Abrechnung",
                url: "/organization",
                dataCy: "user-company-button",
                icon: "fas fa-building",
                index: 2,
            },
            {
                name: "Administration",
                url: "/adminarea",
                dataCy: "user-admin-button",
                icon: "fas fa-shield-halved",
                hidden: !(isSuperUser || isInAgency),
                index: 3,
            },
            {
                name: "API Verbindungen",
                url: "/clients",
                dataCy: "user-clients-button",
                icon: "fas fa-plug-circle-bolt",
                count: clientReducer?.clients ? clientReducer.clients.length : undefined,
                hidden: isTaxAgency,
                index: 6,
            },
            {
                name: "Abmelden",
                url: "/dologout",
                dataCy: "logout-button",
                icon: "fas fa-arrow-right-from-bracket",
                index: 7,
            },
        ];

        if (selectedOrganization) {
            items.push({
                name: `Administration · ${selectedOrganization.name}`,
                url: `/adminarea/organization/${selectedOrganization.externalUUID}`,
                dataCy: "user-admin-organization-button",
                icon: "fas fa-user-tie",
                hidden: !(isSuperUser || isInAgency),
                index: 4,
            });
        }

        // Steuerkanzlei V6 audit log (#41918) — visible ONLY to pathway Super Users (read-only) and ONLY when
        // the selected organization is a Steuerkanzlei (tax agency).
        if (isSuperUser && isTaxAgency) {
            items.push({
                name: "Audit Log",
                url: "/adminarea/kanzlei-audit",
                dataCy: "kanzlei-audit-nav-button",
                icon: "fa fa-shield-alt",
                index: 5,
            });
        }

        return items;
    }, [clientReducer, isInAgency, isSuperUser, isTaxAgency, selectedOrganization]);

    return {
        userDropdownItems,
        userName,
        userSubline,
    };
};
